import React, { useMemo, useState } from 'react';
import {
  useLocalGetStudentsQuery,
  useEnrollStudentMutation,
  useGetClassTermsQuery,
} from '../../store/api/localApi';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { studentDisplayName, isStudentUnnamed } from '../../utils/student';
import ClassDayPicker from '../../components/classes/ClassDayPicker';
import EffectiveFromField, {
  effectiveFromErrorMessage,
} from '../../components/classes/EffectiveFromField';
import {
  formatTermOption,
  bookableTerms,
  coveringTerm,
  formatDayMonth,
} from '../../utils/terms';
import { dublinDateString } from '../../utils/date';

export interface EnrollModalProps {
  classId: string;
  className: string;
  locationId: string;
  /**
   * Today's roster. A child on it is not offered — except one whose last day is
   * today (`endsToday`), so a same-day removal can be undone by re-adding them.
   */
  roster: Array<{ id: string; endsToday?: boolean }>;
  onClose: () => void;
}

/**
 * Per-class enroll modal.
 *
 * Two-step: pick a student, then pick the days they attend (against the
 * class's per-day capacity, with full days disabled). Submit is gated until
 * at least one available day is selected.
 */
const EnrollModal: React.FC<EnrollModalProps> = ({
  classId,
  className,
  locationId,
  roster,
  onClose,
}) => {
  const { data: allStudents, isLoading: studentsLoading } = useLocalGetStudentsQuery(locationId);
  const { data: terms } = useGetClassTermsQuery(classId);
  const [enroll, { isLoading: isEnrolling }] = useEnrollStudentMutation();
  const [error, setError] = useState('');
  const [step, setStep] = useState<'pick-student' | 'pick-days'>('pick-student');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [days, setDays] = useState<number[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  // The server's earliest after a too-early refusal; the From field floors at it.
  const [serverEarliest, setServerEarliest] = useState<string | undefined>();

  // Dublin's date, the same one the From field and the server use.
  const today = dublinDateString();
  // Only the term running now and any still to come can be booked into.
  const options = useMemo(() => bookableTerms(terms ?? [], today), [terms, today]);
  const covering = coveringTerm(options, today);
  const defaultTermId = covering?.termId ?? options[0]?.termId ?? null;
  const effectiveTermId = selectedTermId ?? defaultTermId;
  // The picker earns its place whenever there is a real choice OR the only
  // option is not the term running today — e.g. a class between terms, where
  // the sole bookable term is an upcoming one. Silently defaulting to "today"
  // there would 409 (NO_RUNNING_TERM) since no term covers today at all; the
  // admin needs to see "starts 24 Aug" rather than hit a dead end.
  const showTermPicker = options.length > 1 || (options.length === 1 && !covering);
  const effectiveTerm = options.find((t) => t.termId === effectiveTermId);
  // A booking into a term that has not started cannot begin before it does.
  const fromFloors = [effectiveTerm?.startDate, serverEarliest]
    .filter((d): d is string => !!d)
    .sort();
  const fromNotBefore = fromFloors[fromFloors.length - 1];

  // Only offer children who can actually be enrolled. Since the students list
  // default widened to include LEFT children (Task 18), the picker must exclude
  // them — a LEFT child re-joining goes through reactivate, not enroll. Also
  // excludes children with no name recorded yet (staff "Add Student" before
  // the parent registers) — a nameless row on a roster is nonsensical. The
  // backend refuses both (409 CHILD_LEFT_CANNOT_ENROLL / STUDENT_MISSING_NAME)
  // regardless; this just keeps the picker honest.
  const staysEnrolledIds = new Set(roster.filter((r) => !r.endsToday).map((r) => r.id));
  const availableStudents =
    allStudents?.filter(
      (s) =>
        !staysEnrolledIds.has(s.id) &&
        (s.status === 'ACTIVE' || s.status === 'PENDING_START') &&
        !isStudentUnnamed(s)
    ) || [];
  const selectedStudent = availableStudents.find((s) => s.id === selectedStudentId);

  const goToDayPicker = (studentId: string) => {
    setError('');
    setSelectedStudentId(studentId);
    setDays([]);
    setEffectiveFrom('');
    setServerEarliest(undefined);
    setStep('pick-days');
  };

  const goBack = () => {
    setError('');
    setStep('pick-student');
  };

  const submit = async () => {
    if (!selectedStudentId || days.length === 0 || !effectiveFrom) return;
    setError('');
    try {
      await enroll({
        classId,
        studentId: selectedStudentId,
        daysOfWeek: days,
        effectiveFrom,
        // Sent whenever we resolved a term client-side, whether or not the
        // picker was shown — a class with exactly one bookable term (running
        // or upcoming) still has a term to name explicitly. Omitting it here
        // would make the server re-resolve "the term covering today", which
        // is null whenever the only bookable term hasn't started yet.
        ...(effectiveTermId ? { termId: effectiveTermId } : {}),
      }).unwrap();
      onClose();
    } catch (err: any) {
      const dateProblem = effectiveFromErrorMessage(err?.data);
      if (dateProblem) {
        setError(dateProblem.message);
        if (dateProblem.earliest) {
          setServerEarliest(dateProblem.earliest);
          setEffectiveFrom(dateProblem.earliest);
        }
      } else if (err?.data?.error === 'ALREADY_ENROLLED' && err?.data?.until) {
        // Still enrolled here until a later last day: re-adding would erase it.
        const name = selectedStudent ? studentDisplayName(selectedStudent) : 'This child';
        setError(`${name} is already in this class until ${formatDayMonth(err.data.until)}.`);
      } else if (err?.data?.error === 'CAPACITY_EXCEEDED') {
        setError('One or more selected days are at capacity. Adjust your selection and try again.');
      } else if (err?.data?.error === 'INVALID_ENROLLMENT_DAYS') {
        setError('Some selected days are outside the class schedule.');
      } else if (err?.data?.code === 'CHILD_LEFT_CANNOT_ENROLL') {
        setError('This child has left the service. Reactivate them before enrolling.');
      } else if (err?.data?.code === 'STUDENT_MISSING_NAME') {
        // The picker filters these out, so this is the stale-list race: the
        // child was renamed to blank (or the list aged) between load and
        // submit. Same shape as the LEFT branch above.
        setError(
          'This child has no name recorded yet. Complete their registration before enrolling them in a class.'
        );
      } else if (err?.data?.code === 'NO_RUNNING_TERM') {
        setError(
          'This class has no term set up right now or coming up. Add a term for it before enrolling students.'
        );
      } else {
        setError(err?.data?.message || err?.data?.error || 'Failed to enroll student');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="enroll-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {step === 'pick-student'
              ? 'Enroll Student'
              : `Days for ${selectedStudent ? studentDisplayName(selectedStudent) : ''}`}
          </h3>
          {error && (
            <p className="text-sm text-red-600 mb-3" data-testid="enroll-error">
              {error}
            </p>
          )}

          {step === 'pick-student' ? (
            studentsLoading ? (
              <p className="text-sm text-gray-500 text-center py-6">Loading students...</p>
            ) : availableStudents.length > 0 ? (
              <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                {availableStudents.map((student) => (
                  <li key={student.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-gray-600">
                          {student.firstName?.[0] ?? '?'}
                          {student.lastName?.[0] ?? ''}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {studentDisplayName(student)}
                        </p>
                        {student.grade && <p className="text-xs text-gray-500">{student.grade}</p>}
                      </div>
                    </div>
                    <button
                      data-testid={`enroll-pick-${student.id}`}
                      onClick={() => goToDayPicker(student.id)}
                      className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" /> Pick days
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 text-center py-6">
                All students at this location are already enrolled.
              </p>
            )
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-3 w-3 mr-1" /> Back to student list
              </button>
              {showTermPicker && (
                <div>
                  <label
                    htmlFor="enroll-term-select"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Term
                  </label>
                  <select
                    id="enroll-term-select"
                    data-testid="enroll-term-select"
                    value={effectiveTermId ?? ''}
                    onChange={(e) => {
                      setSelectedTermId(e.target.value);
                      // The From date belongs to the term; the new one sets its own default.
                      setEffectiveFrom('');
                      setServerEarliest(undefined);
                    }}
                    disabled={isEnrolling}
                    className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    {options.map((term) => (
                      <option key={term.termId} value={term.termId}>
                        {formatTermOption(term, today)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <ClassDayPicker
                classId={classId}
                className={className}
                value={days}
                onChange={setDays}
                disabled={isEnrolling}
              />
              <EffectiveFromField
                testId="enroll-from"
                classId={classId}
                locationId={locationId}
                value={effectiveFrom}
                onChange={setEffectiveFrom}
                notBefore={fromNotBefore}
                max={effectiveTerm?.endDate}
                disabled={isEnrolling}
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
            <button
              data-testid="enroll-done-btn"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              {step === 'pick-student' ? 'Done' : 'Cancel'}
            </button>
            {step === 'pick-days' && (
              <button
                data-testid="enroll-confirm-btn"
                onClick={submit}
                disabled={isEnrolling || days.length === 0 || !effectiveFrom}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isEnrolling
                  ? 'Enrolling…'
                  : `Enroll on ${days.length} day${days.length === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollModal;
